/* DEMO ONLY — every figure in this file is INVENTED. It is the Customer 360 half of
   `data/demoFallback.ts`, split out so the tile figures for the 23 cards of
   `registry/customer360Cards.ts` live in one place, and it is deleted with that file.

   Keyed exactly as `DEMO_FALLBACK_VALUES` is: card key from `registry/customer360Cards.ts`, then
   tile key from `registry/customer360MetricIdentifiers.ts`. `metrics` carries the figure already in
   the unit the tile's registry `format` states — `percent` 0-100, `points` 0-100, `count` a whole
   number, `ratio` around 1, `currency` an amount — and `texts` carries the WORD a categorical tile
   draws, for the tiles whose `format` is `count` only because the union has no `text` member.

   ONE customer's grain throughout. Every figure agrees with the `View More` row sets at the foot of
   `data/demoFallback.ts` and with what UAT really answers for this customer where it answers at all:
   C-COM-001 63.66, C-COM-009 35.0 over 6 rows, C-COM-023 0.0, C-BFSI-001 100.0, C-HYB-001 100.0.
   A live reading always wins, so those five are stated here only so the invented tiles beside them
   cannot contradict what the wire draws.

   THE CONFIDENCE RULE WAS RELAXED ON 2026-09-21, AT THE USER'S EXPLICIT REQUEST, FOR THE DEMO.

   Until that date nothing here was a governed value — no confidence score, no confidence band, no
   reason code, no `calculation_status`, no `Why this result?` sentence — and the two tiles that
   would have needed one, `profile_summary.identity_confidence` and
   `payment_merchant_behaviour.p2p_p2m_classification_confidence`, were left EMPTY on purpose. The
   user asked for confidence readings on both screens, named both tiles, and both now carry a figure;
   each is recorded at its own card below so the removal and the re-add both stay visible. The full
   note on what the relaxation does and does not reach is at the head of `data/demoFallback.ts`, and
   the card-level pill lives there too (`DEMO_CARD_CONFIDENCE`) — this file declares tile figures
   only.

   BOTH TILES ARE `format: 'percent'` AND RENDER 0-100. The wire's own `confidence_score` column is a
   0-1 fraction, so a figure copied verbatim off a response would draw `0.6%`.

   A reason code, a `calculation_status` and a `Why this result?` sentence are still never invented
   here. */

/**
 * A card's invented tile figures. Structural on purpose — it is assignable to the
 * `DemoCardFallback` of `data/demoFallback.ts` without importing it, so the two files carry no
 * import cycle.
 */
interface DemoC360TileValues {
  metrics?: Record<string, number>;
  texts?: Record<string, string>;
  overridesProviderError?: true;
}

/** The customer's monthly income, which is the sum of `DEMO_C360_INCOME_SOURCE_ROWS`. */
const INCOME_MONTHLY = 4820;

/** Rewards issued to this customer, the denominator every rate on the Rewards card is taken over. */
const REWARDS_ISSUED = 18;

export const DEMO_C360_TILE_VALUES: Record<string, DemoC360TileValues> = {
  customer_details: { metrics: { customer_profile_attributes: 12, permissions_availability: 4 } },
  // Trailing and forward 12-month value for this one customer, not the tenant's per-customer margin
  // the Layer 1 CLV card states — a different quantity at a different grain.
  holdings_customer_value: {
    metrics: {
      current_clv_band: 2480,
      predicted_clv_band: 3120,
      product_holding_count_distribution: 4
    }
  },
  /* `identity_confidence` was LEFT EMPTY because C-COM-001 is a confidence reading; it is FILLED
     from 2026-09-21 under the relaxation recorded at the head of this file and of
     `data/demoFallback.ts` — the user named this tile. The figure is 63.7, which is C-COM-001's own
     live 63.66 rounded, and NOT a fresh invention: the wire answers this metric for this customer,
     the live reading wins wherever it arrives, and a different number here would make the tile
     disagree with the identity header's chip whenever the wire failed. `format: 'percent'`, so it
     renders 63.7% rather than the 0-1 fraction a `confidence_score` column carries.

     `current_tier` carries both a figure and a word: the tile prints `3`, and
     `CustomerProfileSection` reads `valueText` for the header chip, so without the word the chip
     stays blank beside a populated tile. */
  profile_summary: {
    metrics: { identity_confidence: 63.7, current_tier: 3 },
    texts: { lifecycle_stage: 'Active', current_tier: 'Tier 3' }
  },
  customer_behaviour_profile: {
    metrics: {
      behavioural_momentum_score: 58.2,
      behavioural_deviation_score: 24.7,
      disengagement_early_warning_score: 33.1,
      habit_strength_score: 61.4
    }
  },
  // C-COM-008 really answers `2026-06-29`, which is 84 days before this file was written, and
  // C-COM-009 really answers 35 — so the date, the 84 days and the count are the live readings
  // restated rather than invented alternatives to them.
  engagement_timeline: {
    metrics: { engagement_frequency: 35, days_since_last_engagement: 84 },
    texts: { funnel_stage: 'Campaign Enrolled', last_engagement_date: '29 Jun 2026' }
  },
  // `expectation_gap_score` is the live C-COM-016 reading (9.230769) rounded, so the demo agrees with
  // the wire where it answers; 62.5 is a mid-band sentiment consistent with one open complaint and a
  // reward fulfilment delay in `DEMO_C360_UNRESOLVED_ISSUES`.
  sentiment_expectations: { metrics: { sentiment_score: 62.5, expectation_gap_score: 9.2 } },
  /* ONE REWARDS STORY: 18 issued, of which 11 redeemed (61.1%, the rate C-COM-011 really answers),
     3 expired and 2 reversed (11.1% of the same 18, and the 2 rows the reversal timeline lists).
     11 + 3 + 2 = 16, leaving 2 live entitlements — which is the `Upcoming Expiry (30D)` count. */
  rewards_loyalty_activity: {
    metrics: {
      reward_sensitivity_score: 57.3,
      incentive_dependency_score: 21.8,
      reward_redemption_rate: 61.1,
      points_balance_movement: 1.18,
      rewards_issued: REWARDS_ISSUED,
      rewards_redeemed: 11,
      rewards_expired: 3,
      reversal_count: 2,
      upcoming_expiry_30d: 2,
      reversed_entitlement_summary: 1250,
      reversal_reason_profile: 11.1,
      reversal_timeline_events: 2,
      tier_progression: 64
    }
  },
  data_confidence_supporting_evidence: { metrics: { evidence_pack_completeness: 82 } },
  /* `affected_events_30d` IS the length of `DEMO_C360_AFFECTED_EVENTS`, `latest_event` is that
     list's newest row (`12 May · Velocity Breach`) and `highest_severity_30d` is the highest
     `subject` across it — so the three tiles and the timeline beneath them cannot disagree. */
  risk_unusual_activity: {
    metrics: { fraud_anomaly_score: 34, affected_events_30d: 6, impacted_metrics_decisions: 4 },
    texts: { latest_event: 'Velocity Breach', highest_severity_30d: 'High' }
  },
  /* FINANCIAL HEALTH, one arithmetic: 4,820 in (the four `DEMO_C360_INCOME_SOURCE_ROWS`), 862 of it
     to the monthly column of `DEMO_C360_DEBT_ROWS` and 3,318 to spend, leaving +640 net. The spend
     splits 45 / 30 / 15 / 10 in `DEMO_C360_SPEND_BREAKDOWN`, and 45 is the essential share this
     card's split tile prints. */
  financial_health: {
    metrics: {
      income_monthly: INCOME_MONTHLY,
      net_cashflow_trend: 640,
      debt_obligation_ratio: 862,
      account_balance: 7640,
      financial_stress_signal: 27,
      income_stability_score: 82,
      essential_discretionary_split: 45
    },
    texts: { cashflow_strength: 'Stable' }
  },
  /* PRODUCT READINESS — all 40 tiles, because the card is ONE selector over every industry's
     products and the tenant's industry picks which of them the dropdown offers. The three status
     tiles are words; the 37 readiness scores are `points`, highest where the customer's own figures
     above support the product (deposits and top-ups against a +640 net cashflow) and lowest where
     they do not (a new secured loan against 862 of existing obligations). */
  product_readiness: {
    overridesProviderError: true,
    metrics: {
      credit_card_limit_upgrade_readiness: 72.4,
      affordability_ratio: 38.5,
      personal_loan_readiness: 58.1,
      mortgage_home_loan_readiness: 41.6,
      auto_secured_loan_readiness: 46.9,
      overdraft_credit_line_readiness: 63.2,
      savings_account_deposit_product_readiness: 78.5,
      term_deposit_readiness: 54.7,
      investment_wealth_product_readiness: 49.3,
      insurance_cross_sell_readiness: 66.8,
      wallet_activation_reactivation_readiness: 69.4,
      wallet_top_up_readiness: 74.1,
      bill_payment_adoption_readiness: 61.7,
      p2p_p2m_qr_adoption_readiness: 57.8,
      wallet_linked_card_readiness: 52.3,
      bnpl_credit_readiness: 44.6,
      savings_investment_feature_readiness: 48.2,
      insurance_protection_feature_readiness: 55.9,
      merchant_business_services_readiness: 37.4,
      policy_renewal_readiness: 76.2,
      coverage_top_up_readiness: 59.8,
      rider_add_on_readiness: 62.5,
      life_insurance_cross_sell_readiness: 51.4,
      health_insurance_cross_sell_readiness: 67.3,
      motor_insurance_cross_sell_readiness: 45.1,
      travel_insurance_readiness: 39.7,
      home_property_insurance_readiness: 43.8,
      premium_payment_plan_readiness: 64.9,
      lapse_prevention_service_recovery_readiness: 33.6,
      mobile_plan_upgrade_readiness: 58.4,
      data_pack_add_on_readiness: 71.6,
      roaming_pack_readiness: 28.9,
      device_upgrade_financing_readiness: 47.2,
      family_multi_line_plan_readiness: 53.7,
      broadband_fibre_readiness: 60.3,
      content_ott_bundle_readiness: 42.1,
      retention_renewal_offer_readiness: 65.5
    },
    /* `credit_card_holding` DISAGREES WITH LIVE DATA, BY CHOICE. It is a demo value, and it is what
       resolves the holding branch (`registry/productReadiness.ts`), so the card draws the
       limit-upgrade story for a customer who holds a card. C-COM-023 is the only metric that states
       anything live about this customer's holdings, and it names `current_account` and
       `savings_account` and no Cards family at all; all nine `C-BFS-PR-*` return 502, so nothing on
       the readiness card is a reading. The two therefore contradict each other across cards.

       Kept deliberately, 2026-09-22. Flipping this to a `NOT_HELD_ANSWERS` value mounts
       `CreditCardNoHoldingLayout`, which kills the section's own fallback body — every filled tile,
       the Credit Limit panel and the eligible-cards table go with it — and that layout has no demo
       data by design. Refilling it also reds `productReadiness/__tests__/layouts.test.tsx:293-302`,
       which mounts it directly with no flag guard and asserts its income pair draws labels only. So
       the cheap-looking fix is a rebuild blocked by a test we are committed to leaving alone. */
    texts: {
      credit_card_holding: 'Active',
      readiness_status: 'Eligible',
      delinquency_status: 'Current'
    }
  },
  /* `impact_on_insights` counts the rows of `DEMO_C360_IMPACT_ON_INSIGHTS`. The two withdrawal
     amounts are a month's cash against the same 4,820 income: 1,450 withdrawn in total, of which 890
     inside three days of a salary credit — so the post-income figure stays under the total. */
  cash_usage_dependency: {
    metrics: {
      cash_withdrawal_dependency_score: 28.6,
      cash_dependency_score: 31.2,
      cash_withdrawals: 1450,
      post_income_withdrawals: 890,
      impact_on_insights: 5
    }
  },
  /* `p2p_p2m_classification_confidence` was LEFT EMPTY because `P2P / P2M Confidence` is a
     confidence reading; it is FILLED from 2026-09-21 under the relaxation recorded at the head of
     this file and of `data/demoFallback.ts` — the user named this tile. C-HYB-007 answers nothing
     for this customer, so 86.4 is invented outright; `format: 'percent'`, so the tile renders it as
     86.4% and not as the 0-1 fraction a `confidence_score` column carries. Read it beside
     `merchant_alias_resolution_profile` below: 71.5 of merchant aliases resolve, and classifying a
     transfer as P2P or P2M is the easier of the two judgements, so the confidence sits above it.

     `merchant_alias_resolution_profile` is the same 71.5 the Data Quality card below states, and
     `spend_vs_active_user_average` is the tenant's 268.90 that this customer's own 412.35 average is
     read against. */
  payment_merchant_behaviour: {
    metrics: {
      p2p_p2m_classification_confidence: 86.4,
      top_merchant_affinity: 22.4,
      category_affinity_reward_preference: 31.5,
      average_spend: 412.35,
      qr_payment_ratio: 41.2,
      spend_vs_active_user_average: 268.9,
      transfer_vs_spend_ratio: 84,
      merchant_alias_resolution_profile: 71.5
    }
  },
  // C-HYB-001 answers 100.0 live for this customer, so identification is stated at 100 rather than
  // against it. Coverage and the unclassified rate are complements (88.5 + 11.5 = 100).
  transaction_data_quality: {
    metrics: {
      merchant_identification_confidence: 100,
      category_classification_coverage: 88.5,
      unclassified_transaction_rate: 11.5,
      merchant_alias_match_rate: 71.5,
      gateway_masking_resolution_rate: 84.2
    }
  },
  // WALLET. Four payment instruments in use, and a low velocity risk consistent with the 34 fraud /
  // anomaly score the Risk card states for the same customer.
  payment_usage_risk: { metrics: { payment_diversity: 4, wallet_velocity_risk: 22 } },
  wallet_activity_adoption: {
    metrics: {
      wallet_activity_score: 68,
      wallet_balance_trend: 315.4,
      next_feature_propensity: 1.12
    }
  },
  // INSURANCE. A live policy paid on time, which is the 19 premium-payment risk and the 74 claim
  // experience, and a renewal risk that stays low because of them.
  policy_health_claims: {
    metrics: { premium_payment_risk: 19, claim_experience_score: 74 },
    texts: { policy_status_health: 'Active' }
  },
  renewal_coverage_opportunities: {
    metrics: { renewal_risk: 22, coverage_gap_signal: 34.5, rider_readiness_band: 61 }
  },
  // TELCO. A well-used plan recharged on time is a low churn risk, and the upgrade band sits just
  // above the midpoint because utilisation is high rather than because service has gone wrong.
  churn_service_experience: { metrics: { service_experience_risk: 26, telco_churn_risk: 18 } },
  plan_usage_upgrade_readiness: {
    metrics: { plan_utilization: 73.4, recharge_gap_days: 6, upgrade_readiness_band: 57 }
  },
  contact_restrictions: { texts: { do_not_nudge_state: 'Contactable' } },
  // 5 signals stand, 3 of them eligible to act on, and the strongest carries a 0.78 propensity.
  customer_opportunities_product_interest: {
    metrics: {
      active_opportunity_signals: 5,
      eligible_opportunities: 3,
      product_level_propensity_score: 0.78
    }
  },
  ready_for_engagement: {
    metrics: { nudge_readiness_score: 71 },
    texts: { handoff_status: 'Ready' }
  }
};
